import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { X, Save, Users, Star, Trash2, Plus } from 'lucide-react';
import { checkClientContactDuplicates, formatDuplicateMessages, validateInternalDuplicates } from '../lib/clientValidation';
import { refreshClientsData } from '../lib/clientService';
import { refreshOrdersData } from '../lib/orderService';
import { useActivity } from '../hooks/useActivity';

const ClientForm = ({ isOpen, onClose, onSuccess, editingClient = null }) => {
  const { currentUser, userRole } = useAuth();
  const { logClientActivity } = useActivity();
  const [formData, setFormData] = useState({
    nombreEmpresa: '',
    tipoCliente: 'particular'
  });
  
  const [contacts, setContacts] = useState([{
    nombre: '',
    telefono: '',
    email: '',
    nota: '',
    esPrincipal: true
  }]);
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  const [showDuplicateConfirmation, setShowDuplicateConfirmation] = useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] = useState(null);

  // Populate form when editing
  useEffect(() => {
    if (editingClient) {
      setFormData({
        nombreEmpresa: editingClient.nombreEmpresa || '',
        tipoCliente: editingClient.tipoCliente || 'particular'
      });
      
      // Set contacts, ensuring at least one contact exists and all fields are strings
      if (editingClient.contactos && editingClient.contactos.length > 0) {
        const normalizedContacts = editingClient.contactos.map(contact => ({
          nombre: contact.nombre || '',
          telefono: contact.telefono || '',
          email: contact.email || '',
          nota: contact.nota || '',
          esPrincipal: contact.esPrincipal || false
        }));
        setContacts(normalizedContacts);
      } else {
        setContacts([{
          nombre: '',
          telefono: '',
          email: '',
          nota: '',
          esPrincipal: true
        }]);
      }
    } else {
      // Reset form for new client
      setFormData({
        nombreEmpresa: '',
        tipoCliente: 'particular'
      });
      
      setContacts([{
        nombre: '',
        telefono: '',
        email: '',
        nota: '',
        esPrincipal: true
      }]);
    }
    setErrors({});
    setSubmitError('');
  }, [editingClient, isOpen]);


  const validateForm = async () => {
    const newErrors = {};

    // Validate that at least one contact exists
    if (contacts.length === 0) {
      newErrors.contacts = 'Debe existir al menos un contacto';
      setErrors(newErrors);
      return false;
    }

    // Check for internal duplicates first
    const internalValidation = validateInternalDuplicates(contacts);
    if (internalValidation.hasInternalDuplicates) {
      internalValidation.errors.forEach(error => {
        if (!newErrors[`contact_${error.contactIndex}`]) {
          newErrors[`contact_${error.contactIndex}`] = {};
        }
        if (error.type === 'phone') {
          newErrors[`contact_${error.contactIndex}`].telefono = error.message;
        } else if (error.type === 'email') {
          newErrors[`contact_${error.contactIndex}`].email = error.message;
        }
      });
    }

    // Validate each contact
    for (let i = 0; i < contacts.length; i++) {
      const contact = contacts[i];
      const contactErrors = newErrors[`contact_${i}`] || {};

      // Required field validations for primary contact
      // Only require contact name if no company name is provided
      const hasCompanyName = formData.nombreEmpresa && formData.nombreEmpresa.trim();
      if (contact.esPrincipal && !contact.nombre.trim() && !hasCompanyName) {
        contactErrors.nombre = 'El nombre del contacto es requerido si no se proporciona nombre de empresa';
      }

      // Validate that at least phone or email is provided
      const hasPhone = contact.telefono && contact.telefono.trim();
      const hasEmail = contact.email && contact.email.trim();
      
      if (!hasPhone && !hasEmail) {
        contactErrors.telefono = 'Se requiere teléfono o email';
        contactErrors.email = 'Se requiere teléfono o email';
      } else {
        // Validate phone format if provided
        if (hasPhone && !/^\d{10}$/.test(contact.telefono.replace(/\s/g, ''))) {
          contactErrors.telefono = 'El teléfono debe tener exactamente 10 dígitos';
        }
        
        // Validate email format if provided
        if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
          contactErrors.email = 'El formato del email no es válido';
        }
      }

      if (Object.keys(contactErrors).length > 0) {
        newErrors[`contact_${i}`] = contactErrors;
      }
    }

    // Ensure exactly one contact is marked as principal
    const principalContacts = contacts.filter(c => c.esPrincipal);
    if (principalContacts.length === 0) {
      newErrors.principal = 'Debe existir un contacto principal';
    } else if (principalContacts.length > 1) {
      newErrors.principal = 'Solo puede existir un contacto principal';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleContactChange = (index, field, value) => {
    const updatedContacts = [...contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value
    };
    setContacts(updatedContacts);

    // Clear error for this contact field when user starts typing
    const errorKey = `contact_${index}`;
    if (errors[errorKey] && errors[errorKey][field]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: {
          ...prev[errorKey],
          [field]: ''
        }
      }));
    }
  };

  const handleSetPrincipal = (index) => {
    const updatedContacts = contacts.map((contact, i) => ({
      ...contact,
      esPrincipal: i === index
    }));
    setContacts(updatedContacts);

    // Clear principal error
    if (errors.principal) {
      setErrors(prev => ({
        ...prev,
        principal: ''
      }));
    }
  };

  const handleAddContact = () => {
    setContacts(prev => [...prev, {
      nombre: '',
      telefono: '',
      email: '',
      nota: '',
      esPrincipal: false
    }]);
  };

  const handleRemoveContact = (index) => {
    if (contacts.length <= 1) return; // Don't allow removing if only one contact

    const contactToRemove = contacts[index];
    const updatedContacts = contacts.filter((_, i) => i !== index);

    // If removing the principal contact, set the first remaining contact as principal
    if (contactToRemove.esPrincipal && updatedContacts.length > 0) {
      updatedContacts[0].esPrincipal = true;
    }

    setContacts(updatedContacts);

    // Clear errors for removed contact
    const errorKey = `contact_${index}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const { [errorKey]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setLoading(true);
    setSubmitError('');
    setDuplicateWarnings([]);

    try {
      // Check if user has permission to create/edit clients
      const action = editingClient ? 'editar' : 'crear';
      if (!userRole || !['admin', 'shopkeeper'].includes(userRole)) {
        setSubmitError(`No tienes permisos para ${action} clientes. Tu rol actual es: ${userRole || 'sin rol'}`);
        setLoading(false);
        return;
      }

      // Check for external duplicates
      const duplicateCheck = await checkClientContactDuplicates(
        contacts, 
        editingClient ? editingClient.id : null
      );

      if (!duplicateCheck.success) {
        setSubmitError(duplicateCheck.error);
        setLoading(false);
        return;
      }

      // If duplicates found, show confirmation dialog
      if (duplicateCheck.hasDuplicates) {
        const warningMessages = formatDuplicateMessages(duplicateCheck.duplicates);
        setDuplicateWarnings(warningMessages);
        setShowDuplicateConfirmation(true);
        
        // Store the submission data for later use
        setPendingSubmissionData({
          contactsData: contacts.map(contact => ({
            nombre: contact.nombre.trim(),
            telefono: contact.telefono && contact.telefono.trim() ? contact.telefono.replace(/\s/g, '') : null,
            email: contact.email && contact.email.trim() ? contact.email.trim() : null,
            nota: contact.nota && contact.nota.trim() ? contact.nota.trim() : null,
            esPrincipal: contact.esPrincipal
          })),
          clientData: {
            nombreEmpresa: formData.nombreEmpresa.trim() || null,
            tipoCliente: formData.nombreEmpresa && formData.nombreEmpresa.trim() ? 'empresa' : 'particular',
            updatedAt: new Date()
          }
        });
        
        setLoading(false);
        return;
      }

      // No duplicates, proceed with submission
      await saveClient();
      
    } catch (error) {
      console.error('Error creating client:', error);
      setSubmitError('Error al guardar el cliente. Por favor intente nuevamente.');
      setLoading(false);
    }
  };

  const saveClient = async () => {
    try {
      const contactsData = pendingSubmissionData ? 
        pendingSubmissionData.contactsData : 
        contacts.map(contact => ({
          nombre: contact.nombre.trim(),
          telefono: contact.telefono && contact.telefono.trim() ? contact.telefono.replace(/\s/g, '') : null,
          email: contact.email && contact.email.trim() ? contact.email.trim() : null,
          nota: contact.nota && contact.nota.trim() ? contact.nota.trim() : null,
          esPrincipal: contact.esPrincipal
        }));

      const clientData = pendingSubmissionData ? 
        { ...pendingSubmissionData.clientData, contactos: contactsData } :
        {
          nombreEmpresa: formData.nombreEmpresa.trim() || null,
          tipoCliente: formData.nombreEmpresa && formData.nombreEmpresa.trim() ? 'empresa' : 'particular',
          contactos: contactsData,
          updatedAt: new Date()
        };

      let savedClient;
      const isEditing = !!editingClient;
      
      if (editingClient) {
        // Update existing client
        await updateDoc(doc(db, 'clients', editingClient.id), clientData);
        savedClient = { id: editingClient.id, ...clientData };
      } else {
        // Create new client
        clientData.isArchived = false;
        clientData.createdAt = new Date();
        const docRef = await addDoc(collection(db, 'clients'), clientData);
        savedClient = { id: docRef.id, ...clientData };
      }
      
      // Log activity
      try {
        const clientName = savedClient.nombreEmpresa || 
                          savedClient.contactos?.find(c => c.esPrincipal)?.nombre || 
                          'Cliente sin nombre';
        
        await logClientActivity(
          isEditing ? 'updated' : 'created',
          clientName,
          '',
          {
            clientId: savedClient.id,
            tipoCliente: savedClient.tipoCliente,
            contactCount: savedClient.contactos?.length || 0
          }
        );
      } catch (error) {
        console.error('Error logging client activity:', error);
        // Don't fail client creation if activity logging fails
      }
      
      // Refresh client cache to ensure all views show updated data
      try {
        await refreshClientsData();
        console.log('Client cache refreshed after update');
      } catch (error) {
        console.error('Error refreshing client cache:', error);
        // Don't fail client update if cache refresh fails
      }
      
      // Also refresh order cache since orders display client names
      try {
        await refreshOrdersData();
        console.log('Order cache refreshed after client update');
      } catch (error) {
        console.error('Error refreshing order cache:', error);
        // Don't fail client update if order cache refresh fails
      }
      
      // Reset form and close
      resetForm();
      
      if (onSuccess) {
        onSuccess(savedClient);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving client:', error);
      setSubmitError('Error al guardar el cliente. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmWithDuplicates = async () => {
    setShowDuplicateConfirmation(false);
    setLoading(true);
    await saveClient();
  };

  const handleCancelDuplicates = () => {
    setShowDuplicateConfirmation(false);
    setPendingSubmissionData(null);
    setDuplicateWarnings([]);
  };

  const resetForm = () => {
    setFormData({
      nombreEmpresa: '',
      tipoCliente: 'particular'
    });
    
    setContacts([{
      nombre: '',
      telefono: '',
      email: '',
      nota: '',
      esPrincipal: true
    }]);

    setDuplicateWarnings([]);
    setShowDuplicateConfirmation(false);
    setPendingSubmissionData(null);
  };

  const handleClose = () => {
    if (!loading && !showDuplicateConfirmation) {
      resetForm();
      setErrors({});
      setSubmitError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 bg-white dark:bg-gray-800">
          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-6">
              {submitError}
            </div>
          )}

          <div className="space-y-6">
            {/* Nombre de Empresa */}
            <div>
              <label htmlFor="nombreEmpresa" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre de Empresa
              </label>
              <input
                type="text"
                id="nombreEmpresa"
                name="nombreEmpresa"
                value={formData.nombreEmpresa}
                onChange={handleInputChange}
                maxLength={32}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Nombre de la empresa (opcional)"
              />
            </div>

            {/* Tipo de Cliente - Auto-determinado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de Cliente
              </label>
              <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  formData.nombreEmpresa && formData.nombreEmpresa.trim() 
                    ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                }`}>
                  {formData.nombreEmpresa && formData.nombreEmpresa.trim() ? 'Empresa' : 'Particular'}
                </span>
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                  (Determinado automáticamente)
                </span>
              </div>
            </div>

            {/* Información de Contactos */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Información de Contactos</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">* Se requiere al menos teléfono o email para cada contacto</p>
                </div>
                <button
                  type="button"
                  onClick={handleAddContact}
                  className="inline-flex items-center space-x-1 px-3 py-1 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200 rounded-md hover:bg-green-200 dark:hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>Agregar otro contacto</span>
                </button>
              </div>

              {errors.principal && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-3 py-2 rounded-md text-sm">
                  {errors.principal}
                </div>
              )}

              {errors.contacts && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-3 py-2 rounded-md text-sm">
                  {errors.contacts}
                </div>
              )}
              
              <div className="space-y-6">
                {contacts.map((contact, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 relative">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleSetPrincipal(index)}
                          className={`p-1 rounded ${contact.esPrincipal ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'}`}
                          title={contact.esPrincipal ? 'Contacto principal' : 'Hacer contacto principal'}
                        >
                          <Star className={`h-5 w-5 ${contact.esPrincipal ? 'fill-current' : ''}`} />
                        </button>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Contacto {index + 1}
                          {contact.esPrincipal && <span className="text-yellow-600 dark:text-yellow-400 ml-1">(Principal)</span>}
                        </span>
                      </div>
                      {contacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveContact(index)}
                          className="p-1 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Eliminar contacto"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Nombre del Contacto */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nombre del Contacto {contact.esPrincipal && !(formData.nombreEmpresa && formData.nombreEmpresa.trim()) && '*'}
                        </label>
                        <input
                          type="text"
                          value={contact.nombre || ''}
                          onChange={(e) => handleContactChange(index, 'nombre', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                            errors[`contact_${index}`]?.nombre ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder={
                            contact.esPrincipal && (formData.nombreEmpresa && formData.nombreEmpresa.trim())
                              ? "Nombre del contacto (opcional si hay empresa)"
                              : "Nombre completo del contacto"
                          }
                        />
                        {errors[`contact_${index}`]?.nombre && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[`contact_${index}`].nombre}</p>
                        )}
                      </div>

                      {/* Teléfono */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Teléfono
                        </label>
                        <input
                          type="tel"
                          value={contact.telefono || ''}
                          onChange={(e) => handleContactChange(index, 'telefono', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                            errors[`contact_${index}`]?.telefono ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="1234567890"
                          maxLength="10"
                        />
                        {errors[`contact_${index}`]?.telefono && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[`contact_${index}`].telefono}</p>
                        )}
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={contact.email || ''}
                          onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                            errors[`contact_${index}`]?.email ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="email@ejemplo.com"
                        />
                        {errors[`contact_${index}`]?.email && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[`contact_${index}`].email}</p>
                        )}
                      </div>

                      {/* Nota */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nota
                        </label>
                        <textarea
                          value={contact.nota || ''}
                          onChange={(e) => handleContactChange(index, 'nota', e.target.value)}
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder="Notas adicionales sobre el contacto"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Guardando...' : (editingClient ? 'Actualizar Cliente' : 'Guardar Cliente')}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Duplicate Confirmation Dialog */}
      {showDuplicateConfirmation && (
        <div className="absolute inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center p-4 z-10">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Contactos duplicados encontrados
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Se encontraron los siguientes contactos duplicados:
              </p>
              
              <div className="space-y-2 mb-6">
                {duplicateWarnings.map((warning, index) => (
                  <div key={index} className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 px-3 py-2 rounded-md text-sm">
                    {warning}
                  </div>
                ))}
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                ¿Desea continuar guardando el cliente de todas formas? Esto puede ser legítimo en casos como familiares o contactos compartidos.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelDuplicates}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmWithDuplicates}
                  disabled={loading}
                  className="px-4 py-2 bg-yellow-600 dark:bg-yellow-500 text-white rounded-md hover:bg-yellow-700 dark:hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400 disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : 'Continuar de todas formas'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientForm;